// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/DataQueues.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import '../css/DataQueues.scss';
import { Sidebar } from 'primereact/sidebar';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';

import EventDialog from '../components/EventDialog';
import fetchEvents from './DataQueues/fetchEvents';
import fetchResources from './DataQueues/fetchResources';
import EventContent from './DataQueues/EventContent';
import formatEventReserve from './DataQueues/formatEventReserve';
import ToastNotification from '../components/ToastNotification';

import { API_URLS } from '../api/api.js';
import { getSelectedLanguage } from '../api/api'; 
import { languageManager } from '../utils/loadLanguage';


import { useSelector, useDispatch } from 'react-redux';
import { getSelectedDate } from '../utils/dateUtils';



/**
 * Hook สำหรับใช้ toggle ค่า refreshFlag (เพื่อ trigger re-render หรือ fetch ใหม่)
 */
const useRefreshFlag = () => {
  const [refreshFlag, setRefreshFlag] = useState(false);
  const refresh = useCallback(() => {
    setRefreshFlag(prevFlag => !prevFlag);
  }, []);
  return [refreshFlag, refresh];
};

/**
 * ฟังก์ชันสร้างค่า bookingStatusParam ตามเงื่อนไข
 */
const getBookingStatusParam = () => {
  return '!=6';
};

const DataQueues = ({
  selectedDate,
  onDateChange,
  refreshFlag,
  setRefreshFlag,
  refreshData,
  EventDialogServiceTimeSidebar
}) => {
  const calendarRef = useRef(null);

  // Redux usage
  const handleSubmitClickStatus = useSelector((state) => state.status.handleSubmitClickStatus);
  const dispatch = useDispatch();

  // เก็บ data สถานะต่าง ๆ
  const [events, setEvents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('1');
  const [sidebarReserveVisible, setSidebarReserveVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [resources, setResources] = useState([]);
  const [interactionMode, setInteractionMode] = useState(true);
  const [eventDetails, setEventDetails] = useState({});
  const [showEventDialog, setShowEventDialog] = useState(false);

  // Toast
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  // รีเฟรชใน component (localRefreshFlag)
  const [localRefreshFlag, refreshLocalData] = useRefreshFlag();

  // -----------------------------
  // จัดการภาษา
  // -----------------------------
  const [language, setLanguage] = useState(getSelectedLanguage());
  const [languageData, setLanguageData] = useState({});

  /**
   * useEffect ตัวแรก:
   * ตรวจสอบค่าภาษา Global (getSelectedLanguage()) ทุกครั้ง
   * ถ้าเปลี่ยนจาก state เดิม -> setLanguage ใหม่ -> กระตุ้นให้โหลด JSON ใหม่
   */
  useEffect(() => {
    const currentLang = getSelectedLanguage();
    if (currentLang !== language) {
      setLanguage(currentLang);
    }
  }, [
    // dependencies ใส่หรือไม่ใส่ก็ได้ ขึ้นอยู่กับว่าอยากให้เช็คบ่อยแค่ไหน
    // ตัวอย่าง: refreshFlag, eventDetails, language
    refreshFlag,
    eventDetails,
    language
  ]);

  /**
   * useEffect ตัวที่สอง:
   * โหลด JSON จากไฟล์ lang/xx.json ทันทีที่ state `language` เปลี่ยน
   */
  useEffect(() => {
    (async () => {
      await languageManager.loadLanguage(language);
      setLanguageData(languageManager.translations);
      refreshData(); 
    })();
  }, [language]);

  /**
   * แสดง Toast แจ้งเตือน
   */
  const handleShowToast = (variant, message) => {
    setToastVariant(variant);
    setToastMessage(message);
    setShowToast(true);
  };

  /**
   * Fetch รายการ Branches
   */
  const fetchBranches = async () => {
    try {
      const response = await fetch(API_URLS.BRANCHES, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      // เพิ่ม option "Branch All"
      
      setBranches([
        { branch_ID: 'all', branch_name: languageData?.dataQueues?.SelectBranchAll || "Select Branch All" },
        ...data
      ]);

    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  // เรียก fetchBranches เมื่อ mount
  useEffect(() => {
    fetchBranches();
  }, []);

  /**
   * เมื่อ selectedDate, refreshFlag หรือ localRefreshFlag เปลี่ยน -> fetchEvents ใหม่
   */
  useEffect(() => {
    const bookingStatusParam = getBookingStatusParam();
    fetchEvents(getSelectedDate(selectedDate), setEventDetails, handleShowToast, bookingStatusParam);
  }, [selectedDate, refreshFlag, localRefreshFlag]);

  /**
   * กรณีมีการ Submit Booking (จาก Redux) -> refresh Data
   */
  useEffect(() => {
    if (handleSubmitClickStatus === 1) {
      handleManualRefresh();
      dispatch({ type: 'SET_HANDLE_SUBMIT_CLICK_STATUS', payload: 0 });
    }
  }, [handleSubmitClickStatus, dispatch]);

  /**
   * เมื่อเปลี่ยน branch -> fetch resources ใหม่
   */
  useEffect(() => {
    fetchResources(setResources, selectedBranch);
  }, [selectedBranch]);

  /**
   * เมื่อ selectedDate หรือ refreshFlag เปลี่ยน -> fetchEvents ใหม่
   * (หาก logic ตรงนี้ซ้ำซ้อนกับ useEffect ก่อนหน้า อาจรวมกันหรือใช้เงื่อนไข check ตามต้องการ)
   */
  useEffect(() => {
    const bookingStatusParam = getBookingStatusParam();
    fetchEvents(getSelectedDate(selectedDate), setEventDetails, handleShowToast, bookingStatusParam);
  }, [selectedDate, refreshFlag]);

  /**
   * เมื่อ eventDetails[selectedDate] มีข้อมูล -> แปลงเป็น events เพื่อโชว์ใน FullCalendar
   */
  useEffect(() => {
    if (eventDetails[selectedDate]) {
      const eventsForSelectedDate = eventDetails[selectedDate].map((event, index) => ({
        id: `${selectedDate}_${index}`,
        resourceId: event.resourceId,
        title: `${event.service}`,
        start: `${selectedDate}T${event.startTime}`,
        end: `${selectedDate}T${event.endTime}`,
        extendedProps: event.extendedProps
      }));
      setEvents(eventsForSelectedDate);
    }
  }, [eventDetails, selectedDate]);

  /**
   * Update View ของ FullCalendar ให้ตรงกับ selectedDate
   */
  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      calendarApi.gotoDate(getSelectedDate(selectedDate));
    }
  }, [selectedDate]);

  /**
   * ฟังก์ชันไว้ refresh event manual
   */
  const handleManualRefresh = async () => {
    const bookingStatusParam = getBookingStatusParam();
    await fetchEvents(getSelectedDate(selectedDate), setEventDetails, handleShowToast, bookingStatusParam);
    console.log('Calendar manually refreshed');
  };

  // ------------------------------------------------------------------
  // ส่วนจัดการ Event เมื่อ Click / Hover / Reserve ฯลฯ
  // ------------------------------------------------------------------
  const handleEventReserve = (eventInfo) => {
    const eventProps = eventInfo.event.extendedProps;
    const detailsForDisplay = formatEventReserve(eventProps);
    console.log('Extended Props:', eventProps);
    setSelectedEvent({
      title: eventInfo.event.title,
      content: detailsForDisplay
    });
    setSidebarReserveVisible(true);
  };

  const handleEventClick = (clickInfo) => {
    handleEventReserve(clickInfo);
  };

  const handleEventMouseEnter = (mouseEnterInfo) => {
    // ถ้า interactionMode = Hover -> handleEventReserve
    if (interactionMode) {
      handleEventReserve(mouseEnterInfo);
    }
  };

  const handleDateSelect = () => {
    setSidebarReserveVisible(false);
  };

  const closeSidebar = () => {
    setSidebarReserveVisible(false);
  };

  const eventDidMount = (info) => {
    const description = info.event.extendedProps.description || 'No description available';
    info.el.setAttribute('data-pr-tooltip', description);
  };

  const handleCustomButtonClick = () => {
    setShowEventDialog(true);
  };

  /**
   * Template แสดง branch ใน dropdown
   */
  const branchValueTemplate = (option, props) => {
    if (option && option.branch_name) {
      return <span>{option.branch_name}</span>;
    }
    if (props && props.value) {
      const branch = branches.find(b => b.branch_ID === props.value);
      return branch ? <span>{branch.branch_name}</span> : <span>{languageData?.dataQueues?.SelectBranch || "Select a Branch"}</span>;
    }
    return <span>{languageData?.dataQueues?.SelectBranch || "Select a Branch"}</span>;
  };

  const branchOptionTemplate = (option) => {
    return <span>{option.branch_name}</span>;
  };

  // ------------------------------------------------------------------
  // Return JSX
  // ------------------------------------------------------------------
  return (
    <>
      <ToastNotification
        showToast={showToast}
        toastMessage={toastMessage}
        toastVariant={toastVariant}
        setShowToast={setShowToast}
      />

      <EventDialog
        show={showEventDialog}
        onHide={() => setShowEventDialog(false)}
        eventDetails={selectedEvent || {}}
      />

      {/* Switch สำหรับเปิด/ปิด Hover */}
      <div className="custom-switch-container">
        <InputSwitch
          checked={interactionMode}
          onChange={(e) => setInteractionMode(e.value)}
        />
        <label className="custom-switch-label">Hover</label>
      </div>

      {/* Dropdown เลือก Branch */}
      <div
        className="branch-dropdown-container"
        style={{ position: 'absolute', right: '128px', top: '92px' }}
      >
        <Dropdown
          value={selectedBranch}
          options={branches}
          onChange={(e) => setSelectedBranch(e.value)}
          optionLabel="branch_name"
          optionValue="branch_ID"
          placeholder="Select a Branch"
          valueTemplate={branchValueTemplate}
          itemTemplate={branchOptionTemplate}
        />
      </div>

      {/* Sidebar แสดงรายละเอียดจอง (Reserve) */}
      <Sidebar
        visible={sidebarReserveVisible}
        position="left"
        onHide={closeSidebar}
      >
        <p>{selectedEvent?.title}</p>
        <div dangerouslySetInnerHTML={{ __html: selectedEvent?.content }}></div>
      </Sidebar>

      {/* FullCalendar */}
      <div id="calendar-view">
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, interactionPlugin, resourceTimeGridPlugin]}
          initialView="resourceTimeGridDay"
          events={events}
          eventDidMount={eventDidMount}
          editable={true}
          eventConstraint={null}  
          slotMinTime="00:00"
          slotMaxTime="24:00"
          selectable={true}
          selectMirror={true}
          nowIndicator={true}
          eventClick={handleEventClick}
          dateClick={handleDateSelect}
          resources={resources}
          slotLabelFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          allDaySlot={false}
          customButtons={{
            myCustomButton: {
              // ดึงข้อความจากไฟล์ JSON ของภาษา -> dataQueues.orderPlus
              text: languageData?.dataQueues?.orderPlus || 'Order+',
              click: () => handleCustomButtonClick()
            }
          }}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'myCustomButton, timeGridDay,timeGridWeek'
          }}
          titleFormat={{ year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }}
          initialDate={getSelectedDate(selectedDate)}
          datesSet={(dateInfo) => onDateChange(dateInfo.startStr)}
          eventMouseEnter={handleEventMouseEnter}
          eventContent={(info) => <EventContent eventInfo={info} />}
        />
      </div>

      {/* Sidebar สำหรับแก้ไขเวลาที่จอง (EventDialogServiceTimeSidebar) */}
      <EventDialogServiceTimeSidebar
        visible={sidebarReserveVisible}
        onHide={() => setSidebarReserveVisible(false)}
        selectedDate={getSelectedDate(selectedDate)}
        onHideDialog={() => setShowEventDialog(false)}
        refreshParentData={refreshLocalData}
        handleManualRefresh={handleManualRefresh}
      />
    </>
  );
};

export default DataQueues;
